import type { SupplyAveragePrice } from '@obraplan/core/application/ports/requisition';
import type { JobRoleId } from '@obraplan/core/domain/shared/ids';
import { normalizeName } from '@obraplan/core/domain/shared/naming';
import { roundQuantity } from '@obraplan/core/domain/schedule/unit-of-measure';
import { itemQuantityKey } from '@obraplan/core/domain/takeoff/consolidation';
import {
  materialCodeKey,
  productFactorKey,
  roundPrice,
  sameUnit,
  steelBarFactor,
  STEEL_BAR_LENGTH_M,
  type FactorBasis,
  type MaterialPrice,
  type ProductUnitFactor,
} from '@obraplan/core/domain/takeoff/material-price';
import type { MesclaPorPeca } from '@obraplan/core/domain/takeoff/planilha-por-peca';
import type { ServiceCatalog } from '@obraplan/core/domain/takeoff/service-catalog';
import {
  parseSteelGauge,
  steelGaugeInfo,
  type SteelGauge,
} from '@obraplan/core/domain/takeoff/steel';
import { isPurchasable } from '@obraplan/core/domain/takeoff/takeoff-line';
import type { FuncaoCandidata } from '@obraplan/core/domain/workforce/job-role-match';

/**
 * Completar para orçar, na importação — uma vez por produto (Fase 29, decidido em 13/10/2026).
 *
 * "A digitação deve ser única por produto na hora do import, para não fazer trabalho mais de 1
 * vez." A planilha por peça repete o CA 50 - 16 MM em cada bloco, e o que falta para o orçamento
 * dar preço a ele — o fator da barra — é uma pergunta **do produto**, e não da linha nem da obra.
 * Perguntar depois, material a material, no diálogo do preço de cada obra, era responder a mesma
 * coisa dez vezes. Quatro perguntas, cada uma uma vez:
 *
 * 1. **o aço**: o compras vende a barra e o orçamento conta o quilo. A barra de 12 m da bitola é
 *    só a **sugestão** (`steelBarFactor`) — alguém confere e confirma, e só o confirmado entra
 *    no preço (o fator deixou de ser aplicado sozinho);
 * 2. **a unidade diferente**: o desmoldante em L contra o compras em UN, a areia em M³ contra UN.
 *    O fator digitado vale para **todas as obras**, pelo código e pela unidade do item;
 * 3. **sem código**: não há médio a perguntar, e o provisório é o preço — **desta obra** só,
 *    porque a areia sem código de uma obra não é, por nome, a areia da outra;
 * 4. **a mão de obra sem função**: a descrição que não casou pelo nome com a função da folha. A
 *    escolha vale para a descrição em todos os serviços da planilha onde a função é permitida.
 *
 * Tudo é opcional: o que fica em branco não é gravado, e se completa depois no preço do item.
 * O que mora aqui é a parte sem React — que produtos a importação traz, o que perguntar de cada
 * um e o que a confirmação grava —, para o teste provar a regra sem montar tela.
 */

// ── Os produtos da importação ───────────────────────────────────────────────────

/** Um produto com código e unidade: é o par que tem fator (`productFactorKey`). */
export interface ProdutoComCodigo {
  /** `productFactorKey(code, unit)`. */
  readonly chave: string;
  readonly code: string;
  readonly description: string;
  /** A unidade do item, como a planilha a escreve — o servidor guarda o jeito único. */
  readonly unit: string;
  readonly steelGauge?: SteelGauge;
}

/** Um material sem código: o preço dele é o provisório, pela chave da consolidação. */
export interface ProdutoSemCodigo {
  /** `descricao:<descrição normalizada>` — a mesma chave do preço na obra (D7). */
  readonly key: string;
  readonly description: string;
  readonly unit?: string;
}

/** Uma descrição de mão de obra que não casou com função nenhuma pelo nome. */
export interface MaoDeObraSemFuncao {
  /** `normalizeName(description)` — a chave que `mesclarPorPeca` lê em `funcoesEscolhidas`. */
  readonly chave: string;
  readonly description: string;
  /** Os serviços da planilha onde ela aparece — a escolha vale em todos. */
  readonly servicos: readonly { readonly key: string; readonly rotulo: string }[];
  /** As funções permitidas em algum desses serviços, sem repetir, por nome. */
  readonly funcoes: readonly FuncaoCandidata[];
}

export interface ProdutosDaImportacao {
  readonly comCodigo: readonly ProdutoComCodigo[];
  readonly semCodigo: readonly ProdutoSemCodigo[];
  readonly maoDeObra: readonly MaoDeObraSemFuncao[];
  /** Os códigos distintos, na primeira grafia — o que se pergunta ao compras. */
  readonly codigos: readonly string[];
}

/** Os itens do catálogo que a planilha trouxe — os que têm quantidade vinda dela. */
function itensDaPlanilha(mescla: MesclaPorPeca) {
  const trazidos = new Set(Object.keys(mescla.quantidades.itens));
  return mescla.catalogo.disciplines.flatMap((disciplina) =>
    disciplina.services.flatMap((servico) =>
      servico.items
        .filter((item) => trazidos.has(itemQuantityKey(servico.key, item.key)))
        .map((item) => ({ disciplina, servico, item })),
    ),
  );
}

const porNome = (a: FuncaoCandidata, b: FuncaoCandidata): number =>
  a.name.localeCompare(b.name, 'pt-BR');

/**
 * Os produtos distintos da importação: o que pode precisar de fator, de provisório ou de função.
 *
 * Só o que **a planilha trouxe** — o item que já estava no catálogo e ela não cita não é
 * pergunta desta importação. O mesmo código em dois serviços é um produto só (o fator é do
 * produto); o mesmo código em duas unidades são dois (o fator do quilo não é o da barra). O
 * produto com código e sem unidade fica de fora: sem unidade não há o que converter (`sameUnit`).
 *
 * A mão de obra vem da mescla **sem** as escolhas — é a lista de quem não casou pelo nome, e ela
 * não pode sumir da tela no instante em que alguém escolhe a função.
 */
export function produtosDaImportacao(
  mescla: MesclaPorPeca,
  funcoesDoServico?: (serviceKey: string) => readonly FuncaoCandidata[],
): ProdutosDaImportacao {
  const comCodigo = new Map<string, ProdutoComCodigo>();
  const semCodigo = new Map<string, ProdutoSemCodigo>();
  const codigos = new Map<string, string>();
  const maoDeObra = new Map<
    string,
    {
      description: string;
      servicos: Map<string, string>;
      funcoes: Map<string, FuncaoCandidata>;
    }
  >();

  for (const { disciplina, servico, item } of itensDaPlanilha(mescla)) {
    if (item.kind === 'MAO_DE_OBRA') {
      if (item.jobRoleId !== undefined || funcoesDoServico === undefined) continue;
      const chave = normalizeName(item.description);
      let linha = maoDeObra.get(chave);
      if (linha === undefined) {
        linha = { description: item.description, servicos: new Map(), funcoes: new Map() };
        maoDeObra.set(chave, linha);
      }
      linha.servicos.set(servico.key, `${disciplina.name} › ${servico.name}`);
      for (const funcao of funcoesDoServico(servico.key)) linha.funcoes.set(funcao.id, funcao);
      continue;
    }
    if (!isPurchasable(item.kind)) continue;

    const code = item.code?.trim();
    const unit = item.unit?.trim();
    if (code === undefined || code === '') {
      const key = `descricao:${normalizeName(item.description)}`;
      if (!semCodigo.has(key)) {
        semCodigo.set(key, {
          key,
          description: item.description,
          ...(unit === undefined || unit === '' ? {} : { unit }),
        });
      }
      continue;
    }
    if (unit === undefined || unit === '') continue;
    const chave = productFactorKey(code, unit);
    if (!comCodigo.has(chave)) {
      comCodigo.set(chave, {
        chave,
        code,
        description: item.description,
        unit,
        ...(item.steelGauge === undefined ? {} : { steelGauge: item.steelGauge }),
      });
    }
    const chaveDoCodigo = materialCodeKey(code);
    if (!codigos.has(chaveDoCodigo)) codigos.set(chaveDoCodigo, code);
  }

  return {
    comCodigo: [...comCodigo.values()],
    semCodigo: [...semCodigo.values()],
    maoDeObra: [...maoDeObra.entries()].map(([chave, linha]) => ({
      chave,
      description: linha.description,
      servicos: [...linha.servicos.entries()].map(([key, rotulo]) => ({ key, rotulo })),
      funcoes: [...linha.funcoes.values()].sort(porNome),
    })),
    codigos: [...codigos.values()],
  };
}

// ── O que o servidor já sabe ────────────────────────────────────────────────────

/** Uma leitura que pode não ter vindo — e, então, diz por quê. */
export type Lido<T> =
  { readonly ok: true; readonly valor: T } | { readonly ok: false; readonly erro: string };

/**
 * O que a prévia pergunta ao servidor ao abrir: o compras (a unidade e o médio de cada código),
 * os fatores já confirmados dos produtos e os preços desta obra (o provisório que já existe).
 */
export interface ConsultaParaOrcar {
  /** Por `materialCodeKey`. `undefined` = sem integração com o compras (a lista vazia). */
  readonly compras: Lido<ReadonlyMap<string, SupplyAveragePrice> | undefined>;
  /** Por `productFactorKey`. */
  readonly fatores: Lido<ReadonlyMap<string, ProductUnitFactor>>;
  /** Pela chave da consolidação. */
  readonly precos: Lido<ReadonlyMap<string, MaterialPrice>>;
}

/** A resposta do compras virada mapa, pela chave do código. */
export function mapaDoCompras(
  medias: readonly SupplyAveragePrice[],
): ReadonlyMap<string, SupplyAveragePrice> {
  return new Map(medias.map((media) => [materialCodeKey(media.code), media]));
}

/** Os fatores do servidor virados mapa, pela chave do produto. */
export function mapaDeFatores(
  fatores: readonly ProductUnitFactor[],
): ReadonlyMap<string, ProductUnitFactor> {
  return new Map(fatores.map((fator) => [productFactorKey(fator.code, fator.itemUnit), fator]));
}

// ── O passo ─────────────────────────────────────────────────────────────────────

/** Seis casas: a sugestão aparece inteira no campo e se grava como apareceu. */
const CASAS_DO_FATOR = 9;

/**
 * O fator sugerido da barra de 12 m, nas nove casas que o banco guarda (`numeric(18,9)`) —
 * `undefined` fora do vergalhão em kg contra o compras em barra (`steelBarFactor`).
 *
 * A tela mostra seis casas ("0,052809"), mas grava as nove: arredondar em seis tirava um décimo
 * de milésimo do R$/kg (5,2174 em vez de 5,2175 no 16 mm), e a confirmação seguinte, com o
 * mesmo número, é a mesma gravação — não vira trilha nova.
 */
export function fatorSugeridoDaBarra(
  item: { readonly unit?: string; readonly steelGauge?: SteelGauge; readonly description?: string },
  supplyUnit: string | undefined,
): number | undefined {
  const fator = steelBarFactor(item, supplyUnit);
  return fator === undefined ? undefined : roundQuantity(fator, CASAS_DO_FATOR);
}

/** A bitola do item: a do catálogo, ou a que a descrição escreve. */
export function bitolaDo(item: {
  readonly steelGauge?: SteelGauge;
  readonly description: string;
}): SteelGauge | undefined {
  return item.steelGauge ?? parseSteelGauge(item.description);
}

/** Quanto pesa a barra de 12 m da bitola. */
export function kgPorBarra(bitola: SteelGauge): number {
  return steelGaugeInfo(bitola).kgPerMeter * STEEL_BAR_LENGTH_M;
}

/** O médio do compras, quando há — por unidade **do compras**. */
function medioDe(media: SupplyAveragePrice | undefined): number | undefined {
  return media?.status === 'OK' ? media.average : undefined;
}

function unidadeDe(media: SupplyAveragePrice | undefined): string | undefined {
  if (media === undefined || media.status === 'NAO_ENCONTRADO') return undefined;
  const unit = media.unit?.trim();
  return unit === undefined || unit === '' ? undefined : unit;
}

export interface LinhaDoAco {
  readonly produto: ProdutoComCodigo;
  readonly unidadeDoCompras: string;
  readonly bitola: SteelGauge;
  readonly kgPorBarra: number;
  /** A sugestão: barras num quilo (`fatorSugeridoDaBarra`). */
  readonly fator: number;
  /** Por barra; ausente sem compra no período. */
  readonly medio?: number;
  /** O médio × o fator — o R$/kg que a confirmação daria. */
  readonly precoPorUnidadeDoItem?: number;
  /** O fator que alguém já confirmou para o produto — então a linha não pergunta nada. */
  readonly confirmado?: ProductUnitFactor;
}

export interface LinhaDeUnidade {
  readonly produto: ProdutoComCodigo;
  readonly unidadeDoCompras: string;
  readonly medio?: number;
}

export interface PassoParaOrcar {
  /** O aço a conferir. */
  readonly aco: readonly LinhaDoAco[];
  /** O aço que já tem o fator do produto — dito, e não perguntado de novo. */
  readonly acoConfirmado: readonly LinhaDoAco[];
  /** Os outros produtos de unidade diferente, sem fator ainda. */
  readonly unidade: readonly LinhaDeUnidade[];
  /** Quantos de unidade diferente já têm o fator do produto. */
  readonly unidadeComFator: number;
  readonly semCodigo: readonly ProdutoSemCodigo[];
  /** Quantos sem código já têm o provisório nesta obra. */
  readonly semCodigoComProvisorio: number;
  readonly maoDeObra: readonly MaoDeObraSemFuncao[];
  /**
   * Por que o aço e a unidade diferente não aparecem: sem integração com o compras, ou a
   * leitura que não veio. Ausente = apareceram (ou não havia o que perguntar).
   */
  readonly semCompras?: string;
  /** Por que o sem código não aparece: os preços da obra não foram lidos. */
  readonly semPrecos?: string;
}

/**
 * O que perguntar, grupo a grupo.
 *
 * Sem a consulta (ainda lendo), só a mão de obra: ela não depende do servidor. O aço e a unidade
 * diferente precisam da unidade do compras — sem integração, ou com a leitura que falhou, não há
 * o que comparar, e a tela diz por quê em vez de perguntar um fator para nada. O produto que já
 * tem o fator confirmado não pergunta de novo: é a "digitação única por produto".
 */
export function passoParaOrcar(
  produtos: ProdutosDaImportacao,
  consulta: ConsultaParaOrcar | undefined,
): PassoParaOrcar {
  const vazio: PassoParaOrcar = {
    aco: [],
    acoConfirmado: [],
    unidade: [],
    unidadeComFator: 0,
    semCodigo: [],
    semCodigoComProvisorio: 0,
    maoDeObra: produtos.maoDeObra,
  };
  if (consulta === undefined) return vazio;

  const aco: LinhaDoAco[] = [];
  const acoConfirmado: LinhaDoAco[] = [];
  const unidade: LinhaDeUnidade[] = [];
  let unidadeComFator = 0;
  let semCompras: string | undefined;

  if (!consulta.compras.ok) semCompras = consulta.compras.erro;
  else if (consulta.compras.valor === undefined) {
    semCompras =
      'Sem integração com o compras: não há unidade nem médio para comparar, e o fator fica para quando houver.';
  } else if (!consulta.fatores.ok) semCompras = consulta.fatores.erro;

  if (semCompras === undefined && consulta.compras.ok && consulta.fatores.ok) {
    const compras = consulta.compras.valor ?? new Map<string, SupplyAveragePrice>();
    const fatores = consulta.fatores.valor;
    for (const produto of produtos.comCodigo) {
      const media = compras.get(materialCodeKey(produto.code));
      const unidadeDoCompras = unidadeDe(media);
      if (unidadeDoCompras === undefined || sameUnit(unidadeDoCompras, produto.unit)) continue;
      const confirmado = fatores.get(produto.chave);
      const medio = medioDe(media);
      const bitola = bitolaDo(produto);
      const fator = fatorSugeridoDaBarra(
        { unit: produto.unit, ...(bitola === undefined ? {} : { steelGauge: bitola }) },
        unidadeDoCompras,
      );
      if (bitola !== undefined && fator !== undefined) {
        const linha: LinhaDoAco = {
          produto,
          unidadeDoCompras,
          bitola,
          kgPorBarra: kgPorBarra(bitola),
          fator,
          ...(medio === undefined
            ? {}
            : { medio, precoPorUnidadeDoItem: roundPrice(medio * (confirmado?.factor ?? fator)) }),
          ...(confirmado === undefined ? {} : { confirmado }),
        };
        (confirmado === undefined ? aco : acoConfirmado).push(linha);
        continue;
      }
      if (confirmado !== undefined) {
        unidadeComFator += 1;
        continue;
      }
      unidade.push({ produto, unidadeDoCompras, ...(medio === undefined ? {} : { medio }) });
    }
  }

  let semCodigo: ProdutoSemCodigo[] = [];
  let semCodigoComProvisorio = 0;
  let semPrecos: string | undefined;
  if (!consulta.precos.ok) semPrecos = consulta.precos.erro;
  else {
    const precos = consulta.precos.valor;
    semCodigo = produtos.semCodigo.filter(
      (produto) => precos.get(produto.key)?.provisionalPrice === undefined,
    );
    semCodigoComProvisorio = produtos.semCodigo.length - semCodigo.length;
  }

  return {
    aco,
    acoConfirmado,
    unidade,
    unidadeComFator,
    semCodigo,
    semCodigoComProvisorio,
    maoDeObra: produtos.maoDeObra,
    ...(semCompras === undefined || produtos.comCodigo.length === 0 ? {} : { semCompras }),
    ...(semPrecos === undefined || produtos.semCodigo.length === 0 ? {} : { semPrecos }),
  };
}

// ── As escolhas e o que a confirmação grava ─────────────────────────────────────

/** O que alguém respondeu no passo. Nada respondido é nada gravado. */
export interface EscolhasParaOrcar {
  /** O aço conferido, pela chave do produto. */
  readonly conferidos: ReadonlySet<string>;
  /** O fator digitado da unidade diferente, pela chave do produto. */
  readonly fatores: ReadonlyMap<string, number>;
  /** O provisório digitado do sem código, pela chave do preço. */
  readonly provisorios: ReadonlyMap<string, number>;
  /** A função escolhida, pela descrição normalizada — o que `mesclarPorPeca` lê. */
  readonly funcoes: ReadonlyMap<string, JobRoleId>;
}

export const SEM_ESCOLHAS: EscolhasParaOrcar = {
  conferidos: new Set(),
  fatores: new Map(),
  provisorios: new Map(),
  funcoes: new Map(),
};

/** Um fator do produto como o `SetProductUnitFactors` o recebe. */
export interface FatorAGravar {
  readonly code: string;
  readonly itemUnit: string;
  readonly supplyUnit: string;
  readonly factor: number;
  readonly basis: FactorBasis;
}

export interface PedidoParaOrcar {
  /** Uma chamada só, para todas as obras (`SetProductUnitFactors`). */
  readonly fatores: readonly FatorAGravar[];
  /** Um `SetMaterialPrice` por produto, nesta obra. */
  readonly provisorios: readonly { readonly key: string; readonly provisionalPrice: number }[];
}

/**
 * O que a confirmação grava — só o que alguém respondeu, e só das linhas que a tela mostrou.
 *
 * O aço **conferido** grava a sugestão com a base `BITOLA`; o aço sem a marca não grava nada: a
 * sugestão não vira fator sem que alguém a confira (decidido em 13/10/2026). O fator digitado
 * grava com a base `DIGITADO`, e o zero ou negativo é ignorado (o servidor o recusaria e levaria
 * o pedido inteiro junto). O provisório vai com as quatro casas do preço (D17).
 */
export function pedidoParaOrcar(
  passo: PassoParaOrcar,
  escolhas: EscolhasParaOrcar,
): PedidoParaOrcar {
  const fatores: FatorAGravar[] = [];
  for (const linha of passo.aco) {
    if (!escolhas.conferidos.has(linha.produto.chave)) continue;
    fatores.push({
      code: linha.produto.code,
      itemUnit: linha.produto.unit,
      supplyUnit: linha.unidadeDoCompras,
      factor: linha.fator,
      basis: 'BITOLA',
    });
  }
  for (const linha of passo.unidade) {
    const fator = escolhas.fatores.get(linha.produto.chave);
    if (fator === undefined || !Number.isFinite(fator) || fator <= 0) continue;
    fatores.push({
      code: linha.produto.code,
      itemUnit: linha.produto.unit,
      supplyUnit: linha.unidadeDoCompras,
      factor: fator,
      basis: 'DIGITADO',
    });
  }
  const provisorios: { key: string; provisionalPrice: number }[] = [];
  for (const produto of passo.semCodigo) {
    const preco = escolhas.provisorios.get(produto.key);
    if (preco === undefined || !Number.isFinite(preco) || preco < 0) continue;
    provisorios.push({ key: produto.key, provisionalPrice: roundPrice(preco) });
  }
  return { fatores, provisorios };
}

/** Há alguma coisa a gravar? */
export function pedidoVazio(pedido: PedidoParaOrcar): boolean {
  return pedido.fatores.length === 0 && pedido.provisorios.length === 0;
}

/**
 * Em quantos dos serviços dela cada descrição de mão de obra ficou ligada, na mescla de agora.
 *
 * A escolha pode não pegar em todos: a função que outro item do serviço já usa não se repete
 * (RN-252), e a que não é permitida ali não entra. A tela diz "ligada em 1 de 2" em vez de
 * deixar parecer que a escolha resolveu tudo.
 */
export function ligacoesDaMaoDeObra(
  catalogo: ServiceCatalog,
  linhas: readonly MaoDeObraSemFuncao[],
): ReadonlyMap<string, number> {
  const ligadas = new Map<string, number>();
  const servicos = new Map(
    catalogo.disciplines.flatMap((disciplina) =>
      disciplina.services.map((servico) => [servico.key, servico] as const),
    ),
  );
  for (const linha of linhas) {
    let quantas = 0;
    for (const { key } of linha.servicos) {
      const servico = servicos.get(key);
      const ligada = servico?.items.some(
        (item) =>
          item.kind === 'MAO_DE_OBRA' &&
          item.jobRoleId !== undefined &&
          normalizeName(item.description) === linha.chave,
      );
      if (ligada === true) quantas += 1;
    }
    ligadas.set(linha.chave, quantas);
  }
  return ligadas;
}
